/**
 * BatteryParametersPanel Component
 *
 * Collapsible form exposing the 8 BatteryParams fields driving the DP.
 * Edits are staged locally and only dispatched to the simulator when the
 * user clicks "Recompute", so mid-edit form state doesn't trigger waste
 * runs of the 2-3s DP on every keystroke.
 *
 * Defaults come from DEFAULT_BATTERY (Sungrow SH15T + SBH400 config).
 */

import { useEffect, useMemo, useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChevronDown, ChevronRight, RotateCcw } from 'lucide-react'
import { DEFAULT_BATTERY } from '@/hooks/useBatterySimulation'
import {
  BATTERY_PRESETS,
  CUSTOM_PRESET_ID,
  identifyPreset,
  getPreset,
} from '@/lib/batteryPresets'

/** Field definitions — grouped for readability in the form */
const FIELDS = [
  {
    section: 'Capacity',
    items: [
      { key: 'capacity_kwh_usable', label: 'Usable capacity', unit: 'kWh', step: 1, min: 1 },
      { key: 'initial_soc_kwh', label: 'Initial SoC', unit: 'kWh', step: 1, min: 0 },
    ],
  },
  {
    section: 'Power',
    items: [
      { key: 'max_charge_kw', label: 'Max charge', unit: 'kW', step: 0.5, min: 0 },
      { key: 'max_discharge_kw', label: 'Max discharge', unit: 'kW', step: 0.5, min: 0 },
      { key: 'grid_import_cap_kw', label: 'Grid import cap', unit: 'kW', step: 0.5, min: 0 },
    ],
  },
  {
    section: 'Efficiency',
    items: [
      { key: 'eta_charge', label: 'Charge η', unit: '', step: 0.01, min: 0.01, max: 1 },
      { key: 'eta_discharge', label: 'Discharge η', unit: '', step: 0.01, min: 0.01, max: 1 },
      { key: 'standby_kw', label: 'Standby draw', unit: 'kW', step: 0.01, min: 0 },
    ],
  },
]

export default function BatteryParametersPanel({
  battery,
  onApply,
  isLoading = false,
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen)
  const [draft, setDraft] = useState(battery)

  // Keep draft in sync if the parent battery prop changes externally
  // (e.g. reset after a preset switch).
  useEffect(() => {
    setDraft(battery)
  }, [battery])

  const dirty = JSON.stringify(draft) !== JSON.stringify(battery)

  // Which preset (if any) does the current DRAFT match? We label the
  // dropdown from the draft so the user can see live what they're
  // selecting before clicking Recompute.
  const draftPresetId = useMemo(() => identifyPreset(draft), [draft])
  const selectedPreset = draftPresetId === CUSTOM_PRESET_ID
    ? null
    : getPreset(draftPresetId)

  const handleChange = (key, raw) => {
    const next = raw === '' ? '' : Number(raw)
    setDraft(prev => ({ ...prev, [key]: next }))
  }

  const handlePresetChange = (id) => {
    if (id === CUSTOM_PRESET_ID) return // 'Custom' is display-only, no-op
    const preset = getPreset(id)
    if (preset) setDraft({ ...preset.params })
  }

  const handleApply = () => {
    // Coerce any empty strings back to the previous value to prevent
    // sending NaN. Validation proper lives in the edge function.
    const coerced = Object.fromEntries(
      Object.entries(draft).map(([k, v]) => [k, Number.isFinite(v) ? v : battery[k]])
    )
    setDraft(coerced)
    onApply(coerced)
  }

  const handleReset = () => {
    setDraft(DEFAULT_BATTERY)
  }

  return (
    <Card>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardContent className="py-4">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 w-full text-left font-medium text-neutral-900 hover:text-neutral-700 transition-colors"
            >
              {open
                ? <ChevronDown className="h-4 w-4 text-neutral-500" />
                : <ChevronRight className="h-4 w-4 text-neutral-500" />}
              <span>Battery parameters</span>
              <span className="text-xs text-neutral-500 font-normal ml-2">
                {selectedPreset ? selectedPreset.name : 'Custom'} ·{' '}
                {battery.capacity_kwh_usable} kWh · {battery.max_discharge_kw} kW
              </span>
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent className="pt-4">
            {/* Preset picker: shown inside the collapsible so it doesn't
                clutter the tab header. Changing preset overwrites the
                draft params but still requires an explicit Recompute. */}
            <div className="mb-6 pb-4 border-b border-neutral-200">
              <Label className="text-xs text-neutral-600">Compare preset</Label>
              <div className="flex flex-col md:flex-row md:items-center gap-3 mt-1.5">
                <Select
                  value={draftPresetId}
                  onValueChange={handlePresetChange}
                  disabled={isLoading}
                >
                  <SelectTrigger className="md:w-96">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BATTERY_PRESETS.map(preset => {
                      const midPrice = (preset.price_gbp_min + preset.price_gbp_max) / 2
                      const pricePerKwh = Math.round(midPrice / preset.rated_kwh)
                      return (
                        <SelectItem key={preset.id} value={preset.id}>
                          <div className="flex flex-col items-start">
                            <span className="font-medium">{preset.name}</span>
                            <span className="text-xs text-neutral-500">
                              {preset.rated_kwh} kWh · £{(preset.price_gbp_min / 1000).toFixed(0)}
                              -{(preset.price_gbp_max / 1000).toFixed(0)}k hardware · ~£{pricePerKwh}/kWh
                            </span>
                          </div>
                        </SelectItem>
                      )
                    })}
                    {draftPresetId === CUSTOM_PRESET_ID && (
                      <SelectItem value={CUSTOM_PRESET_ID} disabled>
                        Custom (edited)
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {selectedPreset?.notes && (
                  <p className="text-xs text-neutral-500 md:flex-1">
                    {selectedPreset.notes}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {FIELDS.map(group => (
                <div key={group.section} className="space-y-3">
                  <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                    {group.section}
                  </h4>
                  {group.items.map(field => (
                    <div key={field.key} className="space-y-1">
                      <Label htmlFor={`bp-${field.key}`} className="text-xs text-neutral-600">
                        {field.label}{field.unit && ` (${field.unit})`}
                      </Label>
                      <Input
                        id={`bp-${field.key}`}
                        type="number"
                        value={draft[field.key] ?? ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        step={field.step}
                        min={field.min}
                        max={field.max}
                        disabled={isLoading}
                        className="h-9"
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-4 mt-6 pt-4 border-t border-neutral-200">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleReset}
                disabled={isLoading}
                className="text-xs"
              >
                <RotateCcw className="h-3 w-3 mr-1.5" />
                Reset to Sungrow defaults
              </Button>
              <div className="flex items-center gap-3">
                {dirty && (
                  <span className="text-xs text-neutral-500">Unsaved changes</span>
                )}
                <Button
                  size="sm"
                  onClick={handleApply}
                  disabled={!dirty || isLoading}
                >
                  {isLoading ? 'Computing…' : 'Recompute'}
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </CardContent>
      </Collapsible>
    </Card>
  )
}
